import { useCallback, useEffect, useState } from "react";
import { getConfig, getSession } from "./lib/api";
import type { PublicConfig, SessionResponse } from "./lib/types";
import { ToastProvider } from "./components/Toast";
import LoginScreen from "./pages/LoginScreen";
import ChangePasswordScreen from "./pages/ChangePasswordScreen";
import EvaluatorPortal from "./pages/EvaluatorPortal";
import AdminPortal from "./pages/AdminPortal";

type PortalView = "admin" | "evaluator";

export default function App() {
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [loading, setLoading] = useState(true);
  // 管理者ロールと評価者ロールを兼任しているアカウント向けの表示切替。
  // どちらか一方しか持たない場合はこの値は使われない(session.admin/evaluatorで一意に決まる)。
  const [portalView, setPortalView] = useState<PortalView>("admin");

  const refreshSession = useCallback(async () => {
    const res = await getSession();
    if (res.ok) setSession(res.data);
  }, []);

  const refreshConfig = useCallback(async () => {
    const res = await getConfig();
    if (res.ok) setConfig(res.data);
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([refreshSession(), refreshConfig()]);
      setLoading(false);
    })();
  }, [refreshSession, refreshConfig]);

  if (loading) {
    return <div className="app-loading">読み込み中...</div>;
  }

  const isLoggedIn = !!session && (!!session.evaluator || session.admin);
  const canSwitchPortal = !!session && session.admin && !!session.evaluator;
  const showAdmin = !!session && session.admin && (portalView === "admin" || !session.evaluator);

  return (
    <ToastProvider>
      {!isLoggedIn ? (
        <LoginScreen config={config} onLoggedIn={refreshSession} />
      ) : session!.mustChangePassword ? (
        <ChangePasswordScreen onChanged={refreshSession} />
      ) : showAdmin ? (
        <AdminPortal
          onLogout={refreshSession}
          onSwitchToEvaluator={canSwitchPortal ? () => setPortalView("evaluator") : undefined}
        />
      ) : (
        <EvaluatorPortal
          evaluatorName={session!.evaluator!}
          config={config}
          onLogout={refreshSession}
          onSwitchToAdmin={canSwitchPortal ? () => setPortalView("admin") : undefined}
        />
      )}
    </ToastProvider>
  );
}
